// ================================================================
// src/pages/StudentPrivateClassPage.jsx
// نسخه‌ی نهایی — State Machine + Race Lock + Batch Notify
// ================================================================

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import moment from 'moment-jalaali';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './PrivateClassPage.css';

import { WalletChargeModal } from './PrivateClassShared'; 
import { pay } from '../services/paymentService';

import {
  useTheme,
  useToast,
  useData,
  useScrollProgress,
  useSearchShortcut,
  gradeOptions,
  fieldOptions,
  getSubjects,
  Modal,
  PersianCalendar,
  RatingStars,
  PieChart,
  Confetti,
  PageParticles,
  useRoomeetAuthGate,
  AuthGate,
  PrivateClassProviders,
  COURSE_STATUS,
  getCourseStartMoment,
  getCourseStatusLabel,
  getAdminRoomeetApi,
  getCommissionRate,
  computePaymentSplit,
  computeCancelSplit,
  VAT_RATE,
  CANCEL_PENALTY_RATE,
} from './PrivateClassShared';
import {
  needsField,
  normalizeGradeId,
  normalizeFieldId,
} from '../core/education/curriculum.js';

// ================================================================
// پنل دانش‌آموز
// ================================================================
function StudentPanel({
  user,
  state,
  dispatch,
  refreshData,
  showToast,
  internalApi,
  roomeetApi,
  loading,
  isRoomeetReady,
}) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filters, setFilters] = useState({
    grade: 'همه پایه‌ها',
    field: '',
    subject: 'همه دروس',
    price: 250000,
    sort: 'محبوب‌ترین',
  });
  const [page, setPage] = useState(1);
  const perPage = 6;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [confetti, setConfetti] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ratingModal, setRatingModal] = useState(false);
  const [ratingCourse, setRatingCourse] = useState(null);
  const [selectedRating, setSelectedRating] = useState(0);
  const [enrollingCourseId, setEnrollingCourseId] = useState(null);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);

  // 🕒 تیک هر ثانیه برای countdown زنده
  const [nowMoment, setNowMoment] = useState(moment());
  useEffect(() => {
    const interval = setInterval(() => setNowMoment(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  const liveUser = state.users.find((u) => u.id === user.id) || user;
  const userEnrolledIds = useMemo(
    () => liveUser.enrolledCourses || [],
    [liveUser.enrolledCourses]
  );

  // کلاس‌های قابل مشاهده در بازار (فقط scheduled)
  const allCourses = useMemo(
    () =>
      state.courses.filter(
        (c) => c.status === COURSE_STATUS.SCHEDULED
      ),
    [state.courses]
  );

  // کلاس‌های ثبت‌نام‌شده‌ی دانش‌آموز
  const enrolled = useMemo(
  () =>
    state.courses.filter(
      (c) =>
        userEnrolledIds.includes(c.id) &&
        !(c.hiddenByUsers || []).includes(user.id)
    ),
  [state.courses, userEnrolledIds, user.id]
);

  // ✅ progress فقط کلاس‌های غیرلغو‌شده
  const progress = useMemo(() => {
    const active = enrolled.filter((c) => c.status !== COURSE_STATUS.CANCELLED);
    const total = active.length;
    const completed = active.filter(
      (c) =>
        c.status === COURSE_STATUS.ENDED ||
        moment(c.date, 'jYYYY-jMM-jDD').isBefore(moment(), 'day')
    ).length;
    return {
      total,
      completed,
      percent: total ? Math.round((completed / total) * 100) : 0,
    };
  }, [enrolled]);

  const wishlist = state.wishlist;
  const userData = liveUser;

  const availableFields = useMemo(
    () => (needsField(filters.grade) ? fieldOptions : []),
    [filters.grade]
  );

  const availableSubjects = useMemo(() => {
    if (filters.grade === 'همه پایه‌ها')
      return [...new Set(allCourses.map((c) => c.subject))].filter(Boolean);
    const subjects = getSubjects(filters.grade, filters.field);
    return subjects.length > 0 ? subjects : [];
  }, [filters.grade, filters.field, allCourses]);

  useEffect(() => {
    if (filters.grade === 'همه پایه‌ها')
      setFilters((p) => ({ ...p, field: '', subject: 'همه دروس' }));
    else if (!needsField(filters.grade))
      setFilters((p) => ({ ...p, field: '', subject: 'همه دروس' }));
    else
      setFilters((p) => ({
        ...p,
        field: availableFields[0] || '',
        subject: 'همه دروس',
      }));
  }, [filters.grade, availableFields]);

  useEffect(() => {
    setFilters((p) => ({ ...p, subject: 'همه دروس' }));
  }, [filters.field]);

  // ============================================================
  // فیلتر بازار
  // ============================================================
  const filteredCourses = useMemo(() => {
    const now = moment();
    let result = allCourses.filter((c) =>
      moment(c.date, 'jYYYY-jMM-jDD').isSameOrAfter(now, 'day')
    );
    if (filters.grade !== 'همه پایه‌ها') {
      const targetGrade = normalizeGradeId(filters.grade);
      result = result.filter(
        (c) => normalizeGradeId(c.grade) === targetGrade
      );
    }
    if (filters.field) {
      const targetField = normalizeFieldId(filters.field);
      result = result.filter(
        (c) => normalizeFieldId(c.field) === targetField
      );
    }
    if (filters.subject !== 'همه دروس')
      result = result.filter((c) => c.subject === filters.subject);
    result = result.filter((c) => (c.price || 0) <= filters.price);

    if (filters.sort === 'ارزان‌ترین')
      result.sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (filters.sort === 'گران‌ترین')
      result.sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (filters.sort === 'بالاترین امتیاز')
      result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    else if (filters.sort === 'نزدیک‌ترین زمان')
      result.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    else
      result.sort(
        (a, b) =>
          (b.popular || 0) - (a.popular || 0) ||
          (b.rating || 0) - (a.rating || 0)
      );
    return result;
  }, [allCourses, filters]);

  const totalPages = Math.ceil(filteredCourses.length / perPage);
  const paginatedCourses = filteredCourses.slice(
    (page - 1) * perPage,
    page * perPage
  );

  // ============================================================
  // helper: batch notification
  // ============================================================
  const sendBatchNotifications = async (notifications) => {
    if (!notifications || notifications.length === 0) return;
    const data = await internalApi._fetchData(true);
    const now = moment().format('jYYYY-jMM-jDD');
    for (const n of notifications) {
      data.notifications.push({
        id: Date.now() + Math.random() + Math.random(),
        message: n.message,
        type: n.type || 'info',
        read: false,
        date: now,
        userId: n.userId || null,
      });
    }
    await internalApi._saveData(data);
  };

  // ============================================================
  // ✅ ثبت‌نام (منطق سالم — دست نخورده)
  // ============================================================
const handleEnroll = async (courseId) => {
    if (isLoading || enrollingCourseId) return;

    const courseSnapshot = state.courses.find((c) => c.id === courseId);
    if (!courseSnapshot) {
      showToast('کلاس یافت نشد', 'error');
      return;
    }

    // اگر کلاس رایگان است، مستقیم ثبت‌نام کن
    if (!courseSnapshot.price || courseSnapshot.price === 0) {
      setIsLoading(true);
      setEnrollingCourseId(courseId);
      try {
        const result = await internalApi.enrollStudent(user.id, courseId);
        showToast(result.message, result.success ? 'success' : 'error');
        if (result.success) {
          setConfetti(true);
          setTimeout(() => setConfetti(false), 3000);
          await sendBatchNotifications([{
            message: `📚 دانش‌آموز ${user.name} در کلاس "${courseSnapshot.title}" ثبت‌نام کرد.`,
            type: 'info', userId: courseSnapshot.teacherId,
          }]);
          await refreshData();
        }
      } catch (error) {
        showToast(`خطا: ${error.message}`, 'error');
      } finally {
        setIsLoading(false);
        setEnrollingCourseId(null);
      }
      return;
    }

    // اگر پولی است، به درگاه زرین‌پال برو
    setIsLoading(true);
    setEnrollingCourseId(courseId);
    try {
      const totalAmountToman = Math.round(courseSnapshot.price * (1 + VAT_RATE / 100));
      // هدایت به درگاه
      await pay.privateClass({
        userId: user.id,
        courseId: courseId,
        amountToman: totalAmountToman,
        fullName: user.name || user.fullName || 'دانش‌آموز',
      });
      // کاربر به درگاه می‌رود، اینجا متوقف می‌شویم
    } catch (error) {
      showToast(`خطا در اتصال به درگاه: ${error.message}`, 'error');
      setIsLoading(false);
      setEnrollingCourseId(null);
    }
  };

  // ============================================================
  // ✅ wishlist
  // ============================================================
  const handleWishlist = async (courseId) => {
    try {
      const newWishlist = await internalApi.toggleWishlist(user.id, courseId);
      dispatch({ type: 'TOGGLE_WISHLIST', payload: newWishlist });
      showToast(
        newWishlist.includes(courseId)
          ? 'به علاقه‌مندی‌ها اضافه شد'
          : 'از علاقه‌مندی‌ها حذف شد',
        'info'
      );
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  // ============================================================
  // ✅ لغو ثبت‌نام — با B2 (pendingAmount) در یک تراکنش
  // ============================================================
  const cancelEnrollment = async (courseId) => {
    const courseSnapshot = state.courses.find((c) => c.id === courseId);
    if (!courseSnapshot) {
      showToast('کلاس یافت نشد', 'error');
      return;
    }

    // ✅ پیش‌نمایش جریمه به کاربر
    const preview = computeCancelSplit(courseSnapshot.price);
    if (!window.confirm(
      `آیا از لغو ثبت‌نام مطمئن هستید؟\n\n` +
      `پرداختی شما: ${preview.totalPaid.toLocaleString('fa-IR')} تومان\n` +
      `مالیات (غیرقابل بازگشت): ${preview.vatAmount.toLocaleString('fa-IR')} تومان\n` +
      `جریمه انصراف (${CANCEL_PENALTY_RATE}٪): ${preview.penaltyAmount.toLocaleString('fa-IR')} تومان\n` +
      `─────────────────────\n` +
      `بازگشتی به کیف پول: ${preview.refundAmount.toLocaleString('fa-IR')} تومان`
    )) return;

    const course = courseSnapshot;

    if (course.status === COURSE_STATUS.CANCELLED) {
      showToast('این کلاس لغو شده است و نیازی به لغو ثبت‌نام ندارید.', 'info');
      return;
    }
    if (course.status !== COURSE_STATUS.SCHEDULED) {
      showToast(
        'امکان لغو ثبت‌نام برای کلاس‌های شروع‌شده یا پایان‌یافته وجود ندارد',
        'error'
      );
      return;
    }

    const classMoment = getCourseStartMoment(course);
    const diffHours = classMoment ? classMoment.diff(moment(), 'hours') : 0;
    if (diffHours < 24) {
      showToast(
        'امکان لغو ثبت‌نام کمتر از ۲۴ ساعت مانده به شروع کلاس وجود ندارد',
        'error'
      );
      return;
    }

    // قفل مشترک با enrollStudent
    const LOCK_KEY = `roomeet_enroll_lock_${courseId}`;
    try {
      const now = Date.now();
      const last = Number(localStorage.getItem(LOCK_KEY) || 0);
      if (now - last < 8000) {
        showToast('در همین لحظه یک عملیات دیگر روی این کلاس در حال انجام است. لطفاً چند لحظه بعد دوباره تلاش کنید.', 'warning');
        return;
      }
      localStorage.setItem(LOCK_KEY, String(now));
    } catch (_) {}

    setIsLoading(true);
    try {
      const data = await internalApi._fetchData(true);
      const realUser = data.users.find((u) => u.id === user.id);
      const realCourse = data.courses.find((c) => c.id === courseId);
      if (!realUser || !realCourse) throw new Error('اطلاعات یافت نشد');

      // ✅ محاسبه‌ی انصراف (VAT + جریمه پیش سکو می‌مونه)
      const cancelSplit = computeCancelSplit(realCourse.price);

      // ۱. بازگشت (قیمت پایه − جریمه)
      realUser.wallet = (realUser.wallet || 0) + cancelSplit.refundAmount;
      realUser.enrolledCourses = (realUser.enrolledCourses || []).filter(
        (id) => id !== courseId
      );

      // ۲. کاهش enrolled
      realCourse.enrolled = Math.max(0, (realCourse.enrolled || 0) - 1);
      realCourse.enrolledStudents = (realCourse.enrolledStudents || []).filter(
        (s) => s.userId !== user.id
      );

      // ۳. کاهش pendingAmount معلم (خالص)
      const teacher = data.users.find((u) => u.id === realCourse.teacherId);
      if (teacher && !realCourse.pendingReleased) {
        const studentEntry = (courseSnapshot.enrolledStudents || [])
          .find((s) => s.userId === user.id);
        let netForThisStudent;
        if (studentEntry && typeof studentEntry.netToTeacher === 'number') {
          netForThisStudent = studentEntry.netToTeacher;
        } else {
          const rate = getCommissionRate(data.adminSettings);
          netForThisStudent = computePaymentSplit(realCourse.price || 0, rate).netToTeacher;
        }
        teacher.pendingAmount = Math.max(
          0,
          (teacher.pendingAmount || 0) - netForThisStudent
        );
      }

      // ۴. تراکنش refund
      data.transactions.push({
        id: Date.now() + Math.random(),
        userId: user.id,
        amount: cancelSplit.refundAmount,
        date: moment().format('jYYYY-jMM-jDD'),
        type: 'refund',
        courseId: realCourse.id,
        courseTitle: realCourse.title,
        note: `لغو ثبت‌نام — کسر ${CANCEL_PENALTY_RATE}٪ جریمه و ${VAT_RATE}٪ مالیات`,
      });

      // ۵. تراکنش جریمه (به سکو)
      if (cancelSplit.penaltyAmount > 0) {
        data.transactions.push({
          id: Date.now() + Math.random(),
          userId: null,
          amount: cancelSplit.penaltyAmount,
          date: moment().format('jYYYY-jMM-jDD'),
          type: 'cancel_penalty',
          courseId: realCourse.id,
          courseTitle: realCourse.title,
          note: `جریمه انصراف دانش‌آموز از کلاس "${realCourse.title}"`,
        });
      }

      // ۶. تراکنش VAT (به سکو)
      if (cancelSplit.vatAmount > 0) {
        data.transactions.push({
          id: Date.now() + Math.random(),
          userId: null,
          amount: cancelSplit.vatAmount,
          date: moment().format('jYYYY-jMM-jDD'),
          type: 'vat',
          courseId: realCourse.id,
          courseTitle: realCourse.title,
          note: `مالیات ارزش افزوده کلاس "${realCourse.title}" (انصراف)`,
        });
      }

      // ۷. notify معلم
      if (realCourse.teacherId) {
        data.notifications.push({
          id: Date.now() + Math.random(),
          message: `📤 دانش‌آموز ${user.name} از کلاس "${realCourse.title}" انصراف داد.`,
          type: 'info',
          read: false,
          date: moment().format('jYYYY-jMM-jDD'),
          userId: realCourse.teacherId,
        });
      }

      await internalApi._saveData(data);

      // ۸. حذف از Roomeet (best-effort)
      if (course.siroomehClassId && user.roomeetMemberId) {
        (async () => {
          try {
            const adminApi = await getAdminRoomeetApi();
            if (adminApi) {
              await adminApi.removeMemberFromRoom(
                course.siroomehClassId,
                user.roomeetMemberId
              );
            }
          } catch (e) {
            console.warn('[cancelEnroll] حذف از Roomeet:', e.message);
          }
        })();
      }

      await refreshData();
      showToast(
        `ثبت‌نام لغو شد. ${cancelSplit.refundAmount.toLocaleString('fa-IR')} تومان به کیف پول بازگشت.`,
        'success',
        6000
      );
    } catch (error) {
      console.error('[cancelEnrollment] خطا:', error);
      showToast(`خطا: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        try { localStorage.removeItem(LOCK_KEY); } catch (_) {}
      }, 2000);
    }
  };

  // ============================================================
  // ✅ ورود به کلاس — فقط لینک اختصاصی اعضا (E36: حذف guest fallback)
  // ============================================================
  const handleJoinClass = async (courseId) => {
    const course = state.courses.find((c) => c.id === courseId);
    if (!course) {
      showToast('کلاس یافت نشد', 'error');
      return;
    }
    if (!course.siroomehClassId) {
      showToast('کلاس هنوز در رومیت ایجاد نشده است', 'warning');
      return;
    }
    if (course.status === COURSE_STATUS.CANCELLED) {
      showToast('این کلاس لغو شده است', 'error');
      return;
    }
    if (course.status !== COURSE_STATUS.LIVE) {
      showToast('کلاس هنوز شروع نشده است', 'warning');
      return;
    }
    if (!userEnrolledIds.includes(courseId)) {
      showToast('شما در این کلاس ثبت‌نام نکرده‌اید', 'error');
      return;
    }

    try {
      const result = await internalApi.joinCourse(courseId);
      if (!result?.link) throw new Error('لینک ورود دریافت نشد');
      window.open(result.link, '_blank');
    } catch (e) {
      console.error('[StudentJoin] خطا:', e);
      // ✅ E36: اگر توکن مشکل داشته باشد، AuthGate کاربر را هدایت می‌کند
      if (e.message?.includes('احراز هویت') || e.message?.includes('توکن')) {
        showToast(
          'اتصال شما به Roomeet منقضی شده. صفحه را رفرش کنید تا دوباره وارد شوید.',
          'warning',
          8000
        );
      } else {
        showToast(`خطا در ورود به کلاس: ${e.message}`, 'error');
      }
    }
  };

  // ============================================================
  // ✅ امتیازدهی (فقط برای ended)
  // ============================================================
  const handleRateCourse = async (courseId, rating) => {
    if (!rating || rating < 1 || rating > 5) {
      showToast('لطفاً امتیاز معتبر (۱ تا ۵) انتخاب کنید', 'error');
      return;
    }
    try {
      const data = await internalApi._fetchData(true);
      const course = data.courses.find((c) => c.id === courseId);
      if (!course) {
        showToast('کلاس یافت نشد', 'error');
        return;
      }
      const count = course.ratingCount || 0;
      const total = (course.rating || 0) * count + rating;
      const newCount = count + 1;
      const newRating = Math.round((total / newCount) * 10) / 10;

      await internalApi.updateCourse(courseId, {
        rating: newRating,
        ratingCount: newCount,
      });
      await refreshData();
      showToast('امتیاز شما با موفقیت ثبت شد', 'success');
      setRatingModal(false);
      setSelectedRating(0);
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  // ============================================================
  // ✅ شارژ کیف پول
  // ============================================================
  const handleChargeWallet = async () => {
    const amountStr = prompt('مبلغ شارژ به تومان را وارد کنید:');
    if (!amountStr) return;
    const amount = Number(amountStr);
    if (isNaN(amount) || amount <= 0) {
      showToast('مبلغ نامعتبر است', 'error');
      return;
    }
    try {
      const currentWallet = userData?.wallet || 0;
      await internalApi.updateUser(user.id, {
        wallet: currentWallet + amount,
      });
      await internalApi.addTransaction({
        id: `dep_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        userId: user.id,
        amount,
        date: moment().format('jYYYY-jMM-jDD'),
        type: 'deposit',
      });
      await refreshData();
      showToast(
        `کیف پول شما به مبلغ ${amount.toLocaleString('fa-IR')} تومان شارژ شد`,
        'success'
      );
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  const tabs = [
    { key: 'dashboard', icon: 'fa-th-large', label: 'خلاصه' },
    { key: 'marketplace', icon: 'fa-store', label: 'ثبت‌نام کلاس‌ها' },
    { key: 'my-classes', icon: 'fa-book-open', label: 'کلاس‌های من' },
    { key: 'calendar', icon: 'fa-calendar-alt', label: 'تقویم' },
    { key: 'wallet', icon: 'fa-wallet', label: 'کیف پول' },
  ];


  const handleHideCourse = async (courseId) => {
    if (!window.confirm('این کلاس از لیست شما حذف شود؟')) return;
    try {
      await internalApi.hideCourseForUser(courseId, user.id);
      await refreshData();
      showToast('کلاس از لیست حذف شد', 'success');
    } catch (e) {
      showToast(`خطا: ${e.message}`, 'error');
    }
  };

  // ============================================================
  // رندر دکمه‌های کلاس بر اساس وضعیت
  // ============================================================
  const renderMyClassActions = (c) => {
    if (c.status === COURSE_STATUS.CANCELLED) {
      return (
        <span className="btn-disabled" style={{ color: '#f43f5e' }}>
          ❌ لغو شده
        </span>
      );
    }


    if (c.status === COURSE_STATUS.LIVE) {
      return (
        <button
          className="btn-primary btn-sm pc-btn-glow"
          onClick={() => handleJoinClass(c.id)}
        >
          <i className="fas fa-video" /> ورود به کلاس
        </button>
      );
    }

    if (c.status === COURSE_STATUS.ENDED) {
  return (
    <>
      <span className="btn-disabled">✅ کلاس تمام شد</span>
      <button
        className="btn-outline btn-sm"
        onClick={() => {
          setRatingCourse(c);
          setSelectedRating(c.rating || 0);
          setRatingModal(true);
        }}
      >
        <i className="fas fa-star" /> امتیاز
      </button>
      <button
        className="icon-btn danger"
        onClick={() => handleHideCourse(c.id)}
        title="حذف از لیست"
      >
        <i className="fas fa-trash-alt" />
      </button>
    </>
  );
}

    if (c.status === COURSE_STATUS.SCHEDULED) {
      const classMoment = getCourseStartMoment(c);
      const diffHours = classMoment ? classMoment.diff(moment(), 'hours') : 0;
      const canCancel = diffHours > 24;

      return (
        <>
          <span className="btn-disabled">
            <i className="fas fa-clock" /> {getCourseStatusLabel(c, nowMoment)}
          </span>
          {canCancel && (
            <button
              className="btn-outline btn-sm"
              onClick={() => cancelEnrollment(c.id)}
              disabled={isLoading}
            >
              <i className="fas fa-times" /> لغو ثبت‌نام
            </button>
          )}
        </>
      );
    }

    return null;
  };

  // ============================================================
  // رندر محتوا
  // ============================================================
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <>
            {confetti && <Confetti />}
            <div className="stats-grid">
              <div className="stat-card glass pc-animate-slide-up">
                <div className="stat-icon"><i className="fas fa-book-open" /></div>
                <div className="stat-content">
                  <div className="label">کلاس‌های ثبت‌نامی</div>
                  <div className="value">
                    {enrolled.filter((c) => c.status !== COURSE_STATUS.CANCELLED).length}
                  </div>
                </div>
              </div>
              <div className="stat-card glass pc-animate-slide-up pc-delay-1">
                <div className="stat-icon"><i className="fas fa-video" /></div>
                <div className="stat-content">
                  <div className="label">در حال برگزاری</div>
                  <div className="value">
                    {enrolled.filter((c) => c.status === COURSE_STATUS.LIVE).length}
                  </div>
                </div>
              </div>
              <div className="stat-card glass pc-animate-slide-up pc-delay-2">
                <div className="stat-icon"><i className="fas fa-star" /></div>
                <div className="stat-content">
                  <div className="label">میانگین امتیاز</div>
                  <div className="value">
                    {enrolled.length
                      ? (
                          enrolled.reduce((acc, c) => acc + (c.rating || 0), 0) /
                          enrolled.length
                        ).toFixed(1)
                      : '-'}
                  </div>
                </div>
              </div>
              <div className="stat-card glass pc-animate-slide-up pc-delay-3">
                <div className="stat-icon"><i className="fas fa-coins" /></div>
                <div className="stat-content">
                  <div className="label">اعتبار کیف پول</div>
                  <div className="value">
                    {(userData?.wallet || 0).toLocaleString('fa-IR')} تومان
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard-grid">
              <div className="card glass pc-animate-fade-in">
                <div className="card-header">
                  <h3><i className="fas fa-video" /> کلاس‌های امروز</h3>
                </div>
                {enrolled
                  .filter(
                    (c) =>
                      c.date === moment().format('jYYYY-jMM-jDD') &&
                      c.status !== COURSE_STATUS.CANCELLED
                  )
                  .map((c) => (
                    <div key={c.id} className="activity-item">
                      <div className="activity-icon primary">
                        <i className="fas fa-video" />
                      </div>
                      <div className="activity-text">
                        {c.title} – {c.teacher}
                      </div>
                      <span className="time">{c.time}</span>
                    </div>
                  ))}
                {enrolled.filter(
                  (c) =>
                    c.date === moment().format('jYYYY-jMM-jDD') &&
                    c.status !== COURSE_STATUS.CANCELLED
                ).length === 0 && (
                  <p className="empty-state">امروز کلاسی ندارید</p>
                )}
              </div>

              <div className="card glass pc-animate-fade-in pc-delay-1 progress-card">
                <div className="card-header">
                  <h3><i className="fas fa-chart-pie" /> پیشرفت کلی</h3>
                </div>
                <div className="progress-content">
                  <PieChart percent={progress.percent} label={`${progress.percent}%`} />
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${progress.percent}%` }}
                    />
                  </div>
                  <p>
                    از {progress.total} جلسه، {progress.completed} جلسه را
                    گذرانده‌اید
                  </p>
                </div>
              </div>
            </div>
          </>
        );

      case 'marketplace':
        return (
          <div className="marketplace">
            <div className="filter-section glass pc-animate-fade-in">
              <div className="filter-group">
                <label><i className="fas fa-graduation-cap" /> پایه</label>
                <select
                  value={filters.grade}
                  onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
                >
                  {gradeOptions.map((g) => <option key={g}>{g}</option>)}
                </select>
              </div>
              {needsField(filters.grade) && (
                <div className="filter-group">
                  <label><i className="fas fa-layer-group" /> رشته</label>
                  <select
                    value={filters.field}
                    onChange={(e) => setFilters({ ...filters, field: e.target.value })}
                  >
                    {availableFields.map((f) => <option key={f}>{f}</option>)}
                  </select>
                </div>
              )}
              <div className="filter-group">
                <label><i className="fas fa-book" /> درس</label>
                <select
                  value={filters.subject}
                  onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
                >
                  <option>همه دروس</option>
                  {availableSubjects.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="filter-group">
                <label><i className="fas fa-tag" /> قیمت (تومان)</label>
                <div className="price-slider">
                  <input
                    type="range"
                    min="50000"
                    max="500000"
                    step="5000"
                    value={filters.price}
                    onChange={(e) => setFilters({ ...filters, price: +e.target.value })}
                  />
                  <span>تا {filters.price.toLocaleString('fa-IR')} تومان</span>
                </div>
              </div>
              <div className="filter-group">
                <label><i className="fas fa-sort" /> مرتب‌سازی</label>
                <select
                  value={filters.sort}
                  onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                >
                  <option>محبوب‌ترین</option>
                  <option>نزدیک‌ترین زمان</option>
                  <option>ارزان‌ترین</option>
                  <option>گران‌ترین</option>
                  <option>بالاترین امتیاز</option>
                </select>
              </div>
              <div className="filter-actions">
                <button
                  className="btn-primary btn-sm"
                  onClick={() => { setPage(1); refreshData(); }}
                >
                  <i className="fas fa-check" /> اعمال
                </button>
                <button
                  className="btn-outline btn-sm"
                  onClick={() => {
                    setFilters({
                      grade: 'همه پایه‌ها',
                      field: '',
                      subject: 'همه دروس',
                      price: 250000,
                      sort: 'محبوب‌ترین',
                    });
                    setPage(1);
                  }}
                >
                  <i className="fas fa-undo" /> حذف
                </button>
              </div>
            </div>

            <div className="course-count">{filteredCourses.length} کلاس پیدا شد</div>

            <div className="course-grid pc-stagger-enter">
              {paginatedCourses.map((c) => {
                const isEnrolled = enrolled.some((e) => e.id === c.id);
                const isWishlisted = wishlist.includes(c.id);
                const isThisEnrolling = enrollingCourseId === c.id;

                return (
                  <div key={c.id} className="course-card glass pc-animate-scale-in">
                    {c.sponsored && (
                      <span className="badge sponsored">
                        <i className="fas fa-bullhorn" /> ویژه
                      </span>
                    )}
                    {c.popular && !c.sponsored && (
                      <span className="badge popular">
                        <i className="fas fa-fire" /> محبوب
                      </span>
                    )}
                    {isEnrolled && (
                      <span className="badge enrolled">
                        <i className="fas fa-check" /> ثبت‌نام شده
                      </span>
                    )}

                    <div
                      className="course-img"
                      style={{
                        background: `linear-gradient(135deg, ${c.color || '#0b2b5c'}, #1f4a8a)`,
                      }}
                    >
                      <i className={`fas ${c.icon || 'fa-chalkboard'}`} />
                      <div className="teacher-avatar">
                        <img
                          src={`https://ui-avatars.com/api/?name=${(c.teacher || 'Unknown').replace(/ /g, '+')}&background=fbbf24&color=0b2b5c&size=48`}
                          alt={c.teacher}
                          loading="lazy"
                        />
                      </div>
                    </div>

                    <div className="course-body">
                      <h4>{c.title}</h4>
                      <div className="meta">
                        <span>{c.grade} {c.field && `- ${c.field}`}</span>
                        <RatingStars rating={c.rating || 0} readonly size={18} />
                      </div>
                      <div className="meta">
                        <span><i className="fas fa-calendar-alt" /> {c.date}</span>
                        <span><i className="fas fa-clock" /> {c.time}</span>
                        <span><i className="fas fa-hourglass" /> {c.duration || 60} دقیقه</span>
                      </div>
<div className="price">
  {!c.price || c.price === 0 ? '🎁 رایگان' : (
    <>
      <div style={{ fontSize: '0.8rem', color: 'var(--pc-text-muted)', fontWeight: 'normal' }}>
        قیمت پایه: {c.price.toLocaleString('fa-IR')}
      </div>
      <div>
        پرداختی: {Math.round(c.price * (1 + VAT_RATE / 100)).toLocaleString('fa-IR')} تومان
      </div>
    </>
  )}
</div>
{c.price > 0 && (
  <div style={{
    fontSize: '0.68rem',
    color: 'var(--pc-text-muted)',
    marginTop: '0.2rem',
    textAlign: 'center',
  }}>
    <i className="fas fa-receipt" /> شامل {VAT_RATE}٪ مالیات ارزش افزوده
  </div>
)}
                      <div className="teacher-row">
                        <img
                          src={`https://ui-avatars.com/api/?name=${(c.teacher || 'Unknown').replace(/ /g, '+')}&background=0b2b5c&color=fff&size=32`}
                          alt={c.teacher}
                          loading="lazy"
                        />
                        <span className="t-name">{c.teacher}</span>
                        <span className="t-lessons">
                          <i className="fas fa-user" /> {c.enrolled}/{c.capacity}
                          {c.capacity > 0 && c.enrolled < c.capacity && c.capacity - c.enrolled <= 2 && (
                            <span style={{ color: '#f43f5e', marginRight: '0.3rem', fontSize: '0.75rem' }}>
                              ({c.capacity - c.enrolled} جا مانده)
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="action-row">
                        <button
                          className={`btn-primary btn-sm ${isEnrolled ? 'enrolled' : ''}`}
                          onClick={() => handleEnroll(c.id)}
                          disabled={
                            isEnrolled ||
                            isLoading ||
                            isThisEnrolling ||
                            (c.capacity > 0 && c.enrolled >= c.capacity)
                          }
                        >
                          {isThisEnrolling ? (
                            <><i className="fas fa-spinner fa-spin" /> در حال ثبت...</>
                          ) : isEnrolled ? (
                            '✅ ثبت‌نام شده'
                          ) : c.capacity > 0 && c.enrolled >= c.capacity ? (
                            '🔒 ظرفیت پر شده'
                          ) : (
                            'ثبت‌نام'
                          )}
                        </button>
                        <button
                          className={`btn-outline btn-sm wishlist-btn ${isWishlisted ? 'active' : ''}`}
                          onClick={() => handleWishlist(c.id)}
                        >
                          <i
                            className="fas fa-heart"
                            style={{
                              animation: isWishlisted ? 'pc-heart-beat 0.6s ease' : 'none',
                            }}
                          />
                        </button>
                        <button
                          className="btn-outline btn-sm"
                          onClick={() => {
                            setSelectedCourse(c);
                            setModalOpen(true);
                          }}
                        >
                          <i className="fas fa-info-circle" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  <i className="fas fa-chevron-right" />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    className={page === i + 1 ? 'active' : ''}
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                >
                  <i className="fas fa-chevron-left" />
                </button>
              </div>
            )}
          </div>
        );

      case 'my-classes':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-book-open" /> کلاس‌های من</h3>
            </div>
            {enrolled.length === 0 ? (
              <p className="empty-state">هنوز در هیچ کلاسی ثبت‌نام نکرده‌اید</p>
            ) : (
              enrolled.slice().reverse().map((c) => {
                const cancelled = c.status === COURSE_STATUS.CANCELLED;
                return (
                  <div
                    key={c.id}
                    className="enrolled-item"
                    style={{ opacity: cancelled ? 0.6 : 1 }}
                  >
                    <div>
                      <strong>{c.title}</strong>
                      <br />
                      <span className="meta-text">
                        <i className="fas fa-user" /> {c.teacher} –{' '}
                        <i className="fas fa-calendar-alt" /> {c.date}{' '}
                        <i className="fas fa-clock" /> {c.time}
                      </span>
                      <br />
                      <span className="meta-text">
                        <i className="fas fa-hourglass" /> مدت: {c.duration || 60} دقیقه
                      </span>
                      <br />
                      <span className="meta-text">وضعیت: {getCourseStatusLabel(c, nowMoment)}</span>
                      {c.cancelReason && (
                        <>
                          <br />
                          <span className="meta-text" style={{ color: '#f43f5e' }}>
                            دلیل لغو: {c.cancelReason}
                          </span>
                        </>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      {renderMyClassActions(c)}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        );

      case 'calendar':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-calendar-alt" /> تقویم شمسی</h3>
            </div>
            <PersianCalendar
              courses={enrolled}
              onDateClick={(date) => showToast(`نمایش کلاس‌های ${date}`, 'info')}
            />
          </div>
        );

      case 'wallet':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-wallet" /> کیف پول</h3>
            </div>
            <div className="wallet-grid">
              <div className="wallet-box gradient-box">
                <div className="wallet-label">اعتبار قابل استفاده</div>
                <div className="wallet-amount">
                  {(userData?.wallet || 0).toLocaleString('fa-IR')} <span>تومان</span>
                </div>
              </div>
              <div
                className="wallet-box gradient-box secondary"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
<button className="btn-primary btn-glow" style={{ width: '100%' }} onClick={() => setChargeModalOpen(true)}>
  <i className="fas fa-plus-circle" /> شارژ کیف پول
</button>
              </div>
            </div>
            <div style={{ marginTop: '2rem' }}>
              <h4>تاریخچه تراکنش‌ها</h4>
              {state.transactions.filter((t) => t.userId === user.id).length === 0 ? (
                <p className="empty-state">هیچ تراکنشی ثبت نشده است</p>
              ) : (
                <table className="transaction-table">
                  <thead>
                    <tr><th>تاریخ</th><th>مبلغ</th><th>نوع</th></tr>
                  </thead>
                  <tbody>
                    {state.transactions
                      .filter((t) => t.userId === user.id)
                      .slice()
                      .reverse()
                      .slice(0, 10)
                      .map((t) => (
                        <tr key={t.id}>
                          <td>{t.date}</td>
                          <td><strong>{t.amount.toLocaleString('fa-IR')} تومان</strong></td>
                          <td>
                            {t.type === 'enroll' ? 'ثبت‌نام'
                              : t.type === 'deposit' ? 'شارژ'
                              : t.type === 'withdraw' ? 'برداشت'
                              : t.type === 'refund' ? 'بازگشت وجه'
                              : t.type === 'payment_release' ? 'واریز به معلم'
                              : t.type === 'pending_payment' ? 'در انتظار واریز'
                              : t.type}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
                  {/* 🆕 مودال شارژ کیف پول */}
      <WalletChargeModal 
        isOpen={chargeModalOpen}
        onClose={() => setChargeModalOpen(false)}
        walletBalanceToman={userData?.wallet || 0}
        onCharge={async (tomanAmount) => {
          setChargeModalOpen(false);
          try {
            await pay.walletCharge({ userId: user.id, amountToman: tomanAmount });
          } catch (e) {
            showToast(e.message, 'error');
          }
        }}
      />
    

          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="panel-wrapper">
      <div className="pc-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`fas ${tab.icon}`} /> {tab.label}
          </button>
        ))}
      </div>
      {renderContent()}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedCourse?.title}
      >
        {selectedCourse && (
          <>
            <p><strong>معلم:</strong> {selectedCourse.teacher}</p>
            <p><strong>تاریخ:</strong> {selectedCourse.date} – {selectedCourse.time}</p>
            <p><strong>مدت زمان:</strong> {selectedCourse.duration || 60} دقیقه</p>
            {selectedCourse.price > 0 && (
  <>
    <p><strong>قیمت پایه:</strong> {selectedCourse.price.toLocaleString('fa-IR')} تومان</p>
    <p style={{ fontSize: '0.9rem', color: 'var(--pc-text-muted)' }}>
      <i className="fas fa-receipt" /> مالیات ارزش افزوده ({VAT_RATE}٪):{' '}
      {Math.round(selectedCourse.price * VAT_RATE / 100).toLocaleString('fa-IR')} تومان
    </p>
    <p style={{ fontSize: '1rem', fontWeight: 'bold', color: '#22c55e' }}>
      <i className="fas fa-credit-card" /> مبلغ نهایی پرداختی:{' '}
      {Math.round(selectedCourse.price * (1 + VAT_RATE / 100)).toLocaleString('fa-IR')} تومان
    </p>
  </>
)}
            <p><strong>ظرفیت:</strong> {selectedCourse.enrolled}/{selectedCourse.capacity}</p>
            <p><strong>وضعیت:</strong> {getCourseStatusLabel(selectedCourse, nowMoment)}</p>
            {selectedCourse.cancelReason && (
              <p><strong>دلیل لغو:</strong> {selectedCourse.cancelReason}</p>
            )}
            <RatingStars rating={selectedCourse.rating || 0} readonly />
            {selectedCourse.status === COURSE_STATUS.LIVE &&
              userEnrolledIds.includes(selectedCourse.id) && (
                <button
                  className="btn-primary"
                  onClick={() => handleJoinClass(selectedCourse.id)}
                  style={{ marginTop: '0.5rem' }}
                >
                  <i className="fas fa-video" /> ورود به کلاس
                </button>
              )}
          </>
        )}
      </Modal>

      <Modal
        isOpen={ratingModal}
        onClose={() => setRatingModal(false)}
        title={`امتیازدهی به کلاس ${ratingCourse?.title}`}
      >
        <p style={{ marginBottom: '1rem' }}>لطفاً به این کلاس امتیاز دهید:</p>
        <RatingStars
          rating={selectedRating}
          onRate={(rate) => setSelectedRating(rate)}
          readonly={false}
          size={30}
        />
        <div className="form-actions" style={{ marginTop: '1.5rem' }}>
          <button className="btn-outline" onClick={() => setRatingModal(false)}>
            لغو
          </button>
          <button
            className="btn-primary"
            onClick={() => handleRateCourse(ratingCourse?.id, selectedRating)}
            disabled={!selectedRating}
          >
            ثبت امتیاز
          </button>
        </div>
      </Modal>
    </div>
  );
}

// ================================================================
// کامپوننت اصلی
// ================================================================
function StudentPrivateClassPageContent() {
  const { user, updateUser } = useAuth();
  const { state, dispatch, refreshData, internalApi, roomeetApi, loading, error } = useData();
  const showToast = useToast();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // ✅ E26: پاس‌دادن users به authGate
  const authGate = useRoomeetAuthGate({
    user,
    updateUser,
    roomeetApi,
    requiredRole: 'student',
    users: state.users,
  });
  const isRoomeetReady = authGate.isReady;

  const [studentProgressPercent, setStudentProgressPercent] = useState(0);
  useEffect(() => {
    if (user && user.role === 'student') {
      internalApi
        .getStudentProgress(user.id)
        .then((p) => setStudentProgressPercent(p.percent))
        .catch(() => {});
    } else {
      setStudentProgressPercent(0);
    }
  }, [user, internalApi, state.courses]);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const handleSearch = useCallback(
    async (query) => {
      setSearchQuery(query);
      if (query.trim().length < 2) {
        setSearchResults([]);
        setIsSearchOpen(false);
        return;
      }
      const courses = await internalApi.getCourses();
      const results = courses.filter(
        (c) =>
          c.title?.includes(query) ||
          c.teacher?.includes(query) ||
          c.subject?.includes(query) ||
          c.grade?.includes(query) ||
          c.field?.includes(query) ||
          c.description?.includes(query)
      );
      setSearchResults(results);
      setIsSearchOpen(true);
    },
    [internalApi]
  );

  useSearchShortcut(inputRef);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { scrollProgress, headerScrolled } = useScrollProgress();

  if (!user) {
    return (
      <div className="private-class-page">
        <div className="pc-content" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <div className="card glass" style={{ maxWidth: '500px', margin: '0 auto', padding: '3rem 2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔒</div>
            <h2 style={{ color: 'var(--pc-text-primary)', marginBottom: '0.5rem' }}>
              لطفاً ابتدا وارد شوید
            </h2>
            <p style={{ color: 'var(--pc-text-muted)', marginBottom: '2rem' }}>
              برای دسترسی به این صفحه، باید وارد حساب کاربری خود شوید.
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate('/login')}
              style={{ fontSize: '1.1rem', padding: '0.8rem 2.5rem' }}
            >
              <i className="fas fa-arrow-left" /> رفتن به صفحه ورود
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (user.role !== 'student') {
    return (
      <div className="private-class-page">
        <div className="pc-content" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <div
            className="card glass"
            style={{ maxWidth: '600px', margin: '0 auto', padding: '3rem 2rem', borderColor: '#f43f5e' }}
          >
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚫</div>
            <h2 style={{ color: 'var(--pc-text-primary)', marginBottom: '0.5rem' }}>
              دسترسی غیرمجاز
            </h2>
            <p style={{ color: 'var(--pc-text-muted)', marginBottom: '2rem' }}>
              این صفحه مخصوص دانش‌آموزان است. لطفاً با نقش مناسب وارد شوید.
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate('/home')}
              style={{ fontSize: '1.1rem', padding: '0.8rem 2.5rem' }}
            >
              <i className="fas fa-arrow-left" /> بازگشت به خانه
            </button>
          </div>
        </div>
      </div>
    );
  }

  const notifCount = state.notifications.filter(
    (n) => !n.read && (!n.userId || n.userId === user.id)
  ).length;

  const panelProps = {
    user,
    state,
    dispatch,
    refreshData,
    showToast,
    internalApi,
    roomeetApi,
    loading,
    isRoomeetReady,
  };

  return (
    <AuthGate
      status={authGate.status}
      roleLabel="دانش‌آموز"
      roleIcon="fa-user-graduate"
      requiredRole="student"
      loginError={authGate.loginError}
      onLogin={authGate.login}
    >
      <div className="private-class-page">
        <div className="scroll-progress-bar" style={{ width: scrollProgress + '%' }} />
        <PageParticles />
        <header className={`pc-header ${headerScrolled ? 'scrolled' : ''}`}>
          <div className="header-left">
            <div className="logo-wrapper">
              <div className="logo-icon">
                <i className="fas fa-user-graduate" />
                <div className="logo-pulse" />
              </div>
              <h1>
                کلاس خصوصی<span className="role-badge">دانش‌آموز</span>
              </h1>
            </div>
            <div className="header-subtitle">
              <span className="subtitle-text">🎯 یادگیری هدفمند با بهترین اساتید</span>
            </div>
          </div>
          <div className="header-right">
            <div className="search-box-wrapper" ref={searchRef}>
              <div className={`search-box ${isSearchOpen && searchResults.length > 0 ? 'active' : ''}`}>
                <i className="fas fa-search" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="جستجوی کلاس، معلم، درس..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchQuery.trim().length >= 2 && setIsSearchOpen(true)}
                />
                <span className="type-cursor" />
                <span className="search-shortcut">⌘K</span>
                {searchQuery && (
                  <button
                    className="search-clear"
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                      setIsSearchOpen(false);
                    }}
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
              {isSearchOpen && searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.slice(0, 6).map((c) => (
                    <div
                      key={c.id}
                      className="search-result-item"
                      onClick={() => {
                        setIsSearchOpen(false);
                        showToast(`«${c.title}» — برای مشاهده‌ی جزئیات، به تب «ثبت‌نام کلاس‌ها» بروید`, 'info');
                      }}
                    >
                      <div className="result-icon" style={{ background: c.color || '#0b2b5c' }}>
                        <i className={`fas ${c.icon || 'fa-chalkboard'}`} />
                      </div>
                      <div className="result-info">
                        <div className="result-title">{c.title}</div>
                        <div className="result-meta">
                          {c.teacher} • {(c.price || 0).toLocaleString('fa-IR')} تومان
                        </div>
                      </div>
                      <button
                        className="result-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSearchOpen(false);
                          showToast(`«${c.title}» — برای مشاهده‌ی جزئیات، به تب «ثبت‌نام کلاس‌ها» بروید`, 'info');
                        }}
                      >
                        مشاهده
                      </button>
                    </div>
                  ))}
                  {searchResults.length > 6 && (
                    <div className="search-more">+ {searchResults.length - 6} نتیجه دیگر</div>
                  )}
                </div>
              )}
            </div>
            <button className="theme-toggle" onClick={toggleTheme} aria-label="تغییر تم">
              <span className="icon">{theme === 'light' ? '🌙' : '☀️'}</span>
            </button>
            {/*<div
              className="notification-wrapper"
              onClick={() => showToast(`${notifCount} نوتیفیکیشن جدید`, 'info')}
              aria-label="اعلان‌ها"
            >
              <i className="fas fa-bell">
                {notifCount > 0 && <span className="notif-badge">{notifCount}</span>}
              </i>
              <div className="notification-dot" />
            </div>
            <div className="avatar-wrapper">
              <img
                src={`https://ui-avatars.com/api/?name=${(user.name || 'کاربر').replace(/ /g, '+')}&background=0b2b5c&color=fff&size=46`}
                alt="آواتار"
                loading="lazy"
              />
              <div className="avatar-ring" />
              <div className="avatar-status" />
            </div>*/}
          </div>
          <div className="header-progress-bar">
            <div className="progress-track">
              <div
                className="progress-fill-header"
                style={{ width: `${studentProgressPercent}%` }}
              />
            </div>
          </div>
        </header>
        <div className="pc-content">
          {error && (
            <div className="card glass" style={{ borderColor: '#f43f5e', marginBottom: '1.5rem' }}>
              <p style={{ color: '#f43f5e', fontWeight: 'bold' }}>
                ⚠️ خطا در بارگذاری داده‌ها: {error}
              </p>
              <button className="btn-primary btn-sm" onClick={() => refreshData()}>
                تلاش مجدد
              </button>
            </div>
          )}
          {loading ? (
            <div className="skeleton-card" style={{ height: '300px', borderRadius: '28px' }} />
          ) : (
            <StudentPanel {...panelProps} />
          )}
        </div>
      </div>
    </AuthGate>
  );
}

const MemoizedStudentPrivateClassPageContent = React.memo(StudentPrivateClassPageContent);

export default function StudentPrivateClassPage() {
  return (
    <PrivateClassProviders>
      <MemoizedStudentPrivateClassPageContent />
    </PrivateClassProviders>
  );
}